import React, { Component, useState } from "react";
import { showModal } from "../../../actions/modal.actions";
import {
  getNotificationProperties,
  updateNotificationProperties,
  testNotificationProperties,
} from "src/actions/notifications.actions";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import Loader from "../../../components/Loader";
import { Formik } from "formik";
import TextInput from "../../../components/UI/TextInput";
import { getUserPermission } from "../../../selectors";
import { getErrorMessage } from "src/utils/helpers";

const NotificationTypePropsSection = ({
  name,
  configurations,
  formValues,
  formProps,
  iconClassName = null,
  validateHandler = null,
  disabled = false,
}) => {
  const [validating, setValidating] = useState(false);

  return (
    <div className="card-body">
      <div className="plan-header plan-header-centered">
        <span className="plan-title">
          <i className={`${iconClassName || "fas fa-fw fa-cogs"}`} />
          {name}
        </span>
        {validateHandler && (
          <button
            onClick={async () => {
              setValidating(true);
              await validateHandler();
              setValidating(false);
            }}
            className="btn btn-primary"
            disabled={validating}
          >
            {validating && <i className="fas fa-spinner fa-spin" />}
            Validate &amp; Test
          </button>
        )}
      </div>
      <div className="plan-body">
        <div className="form-group service-wrapper">
          {configurations &&
            configurations.map((prop, idx) => {
              return (
                <div key={`nhp_${idx}`} style={{ display: "flex" }}>
                  <TextInput
                    label={prop.key}
                    type="text"
                    name={prop.id}
                    onChange={formProps.handleChange}
                    onBlur={formProps.handleBlur}
                    value={formValues[prop.id] || ""}
                    disabled={disabled}
                    autocomplete={false}
                  />
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

class NotificationProperties extends Component {
  state = {
    isLoading: true,
    dataLoaded: false,
    data: {},
    saveLoading: false,
    notificationType: {},
    emailConfigurations: [],
    slackConfigurations: [],
    twilioConfigurations: [],
  };

  componentDidMount() {
    document.title = "Notification Properties - camvio.cloud";
    this.props.changeNavBarType("default", "Notification Properties");
    this.loadConfigurations();
  }

  loadConfigurations = async () => {
    await Promise.all([
      this.props.getNotificationProperties("EMAIL").then((resp) => {
        const d = {};
        const configurations = resp.data.notificationHandlerProperties.sort((a, b) => a.id - b.id);
        configurations.map((prop) => (d[prop.id] = prop.value));
        this.setState({
          emailConfigurations: configurations,
          data: {
            ...this.state.data,
            ...d,
          },
        });
      }),
      this.props.getNotificationProperties("SLACK").then((resp) => {
        const d = {};
        const configurations = resp.data.notificationHandlerProperties.sort((a, b) => a.id - b.id);
        configurations.map((prop) => (d[prop.id] = prop.value));
        this.setState({
          slackConfigurations: configurations,
          data: {
            ...this.state.data,
            ...d,
          },
        });
      }),
      this.props.getNotificationProperties("TWILIOMSG").then((resp) => {
        const d = {};
        const configurations = resp.data.notificationHandlerProperties.sort((a, b) => a.id - b.id);
        configurations.map((prop) => (d[prop.id] = prop.value));
        this.setState({
          twilioConfigurations: configurations,
          data: {
            ...this.state.data,
            ...d,
          },
        });
      }),
    ]);
    this.setState({
      isLoading: false,
    });
  };

  onConfigurationSaveBtnClick = (value, updatedProps) => {
    this.props.showModal("CONFIRM_CHANGES_AND_ACTION_MODAL", {
      title: "Save Configuration",
      text: "Are you sure you want to make these changes?",
      changes: updatedProps,
      action: () => this.saveNotificationConfiguration(value),
    });
  };

  apiPropsDataMapper = (values, configurations, changedOnly = true) => {
    if (changedOnly) configurations = configurations.filter((c) => c.value !== values[c.id]);

    return configurations.map((c) => {
      return {
        id: c.id,
        key: c.key,
        notificationType: c.notificationType,
        value: values[c.id],
      };
    });
  };

  saveNotificationConfiguration = (values) => {
    const propsData = [
      ...this.apiPropsDataMapper(values, this.state.emailConfigurations),
      ...this.apiPropsDataMapper(values, this.state.slackConfigurations),
      ...this.apiPropsDataMapper(values, this.state.twilioConfigurations),
    ];
    if (propsData.length > 0) {
      this.setState({ isLoading: true });
      this.props
        .updateNotificationProperties(propsData)
        .then((resp) => {
          toastr.success("Your Data is Updated", { timeOut: 3000, position: "top-center" });
          return this.loadConfigurations();
        })
        .finally(() => this.setState({ isLoading: false }));
    }
  };

  getUpdatedProps = (values) => {
    const dataMapper = (configurations) => {
      return configurations
        .filter((c) => c.value !== values[c.id])
        .map((c) => {
          return { key: c.key, oldVal: c.value, newVal: values[c.id] };
        });
    };
    const updatedProps = [
      ...dataMapper(this.state.emailConfigurations),
      ...dataMapper(this.state.slackConfigurations),
      ...dataMapper(this.state.twilioConfigurations),
    ];
    return updatedProps;
  };

  getSingleInput(title, message, label, type, validate = null) {
    return new Promise((resolve, reject) => {
      this.props.showModal("SINGLE_INPUT_MODAL", {
        title,
        message,
        label,
        type,
        validate,
        action: (value) => {
          if (typeof value === "undefined") reject("Cancelled");
          else resolve(value);
        },
      });
    });
  }

  async getTestEmailAddress() {
    return await this.getSingleInput(
      "Test Email Address",
      "Enter test email address to send a test message",
      "",
      "email"
    );
  }

  async getTestPhoneNumber() {
    return await this.getSingleInput(
      "Test Phone Number",
      "Enter test phone number to send a test message",
      "",
      "text",
      (value) => {
        if (/^\+?[\d]{7,15}$/.test(value)) return null;

        return "Twilio prefers phone numbers to be formatted in E.164 format. For example: +10000000000";
      }
    );
  }

  validateConfigurations = async (notificationType) => {
    let data = {
      testReceiver: "",
      testMessage: "This is a test message sent by CamvIO web application to validate notification configuration.",
    };
    try {
      switch (notificationType) {
        case "EMAIL":
          data.testReceiver = await this.getTestEmailAddress();
          if (!data.testReceiver) return;
          break;
        case "SLACK":
          break;
        case "TWILIOMSG":
          data.testReceiver = await this.getTestPhoneNumber();
          if (!data.testReceiver) return;
          break;
        default:
          return;
      }
    } catch {
      return;
    }

    try {
      const resp = await this.props.testNotificationProperties(notificationType, data);

      if (resp.data.success) {
        toastr.success(`A test message has been sent. Please check!`, { timeOut: 3000, position: "top-center" });
      } else {
        toastr.error(
          `Failed to send a test message.`,
          `This probably means that the ${notificationType} properties are invalid. Error message was:\n${resp.data.message}`,
          { position: "top-center" }
        );
      }
    } catch (err) {
      toastr.error(`${notificationType} properties are invalid.`, `Error message was:\n${getErrorMessage(err)}`, {
        position: "top-center",
      });
    }
  };

  render() {
    const { showConfig, editConfig, adminShowConfig } = this.props;
    return (
      <div className="cmv-container cmv-container-dashboard cmv-container-client-selected">
        {showConfig && adminShowConfig ? (
          <Formik initialValues={this.state.data} enableReinitialize={true} onSubmit={this.handleSubmit}>
            {(formProps) => {
              const { handleSubmit, values } = formProps;
              const updatedProps = this.getUpdatedProps(values);

              return (
                <form onSubmit={handleSubmit}>
                  <div className="form-vertical">
                    <div className="card-vertical card-plane">
                      {this.state.isLoading && <Loader />}
                      {!this.state.isLoading && (
                        <>
                          <NotificationTypePropsSection
                            name="Email"
                            configurations={this.state.emailConfigurations}
                            formProps={formProps}
                            formValues={values}
                            iconClassName="fas fa-fw fa-envelope"
                            validateHandler={async () => await this.validateConfigurations("EMAIL")}
                          />
                          <NotificationTypePropsSection
                            name="Slack"
                            configurations={this.state.slackConfigurations}
                            formProps={formProps}
                            formValues={values}
                            iconClassName="fab fa-fw fa-slack"
                            validateHandler={async () => await this.validateConfigurations("SLACK")}
                          />
                          <NotificationTypePropsSection
                            name="Twilio"
                            configurations={this.state.twilioConfigurations}
                            formProps={formProps}
                            formValues={values}
                            iconClassName="fas fa-fw fa-sms"
                            validateHandler={async () => await this.validateConfigurations("TWILIOMSG")}
                          />
                          <div style={{ textAlign: "center" }}>
                            <h3 style={{ color: "lightgreen" }}>{this.state.conformation}</h3>
                          </div>
                          {editConfig && (
                            <div className="container" style={{ textAlign: "right" }}>
                              <button
                                onClick={() => {
                                  this.onConfigurationSaveBtnClick(values, updatedProps);
                                }}
                                disabled={(this.state.editConfig && this.state.saveLoading) || updatedProps.length <= 0}
                                type="button"
                                className="btn btn-primary"
                              >
                                {this.state.saveLoading && (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                    style={{ marginRight: "5px" }}
                                  />
                                )}
                                Save
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </form>
              );
            }}
          </Formik>
        ) : (
          <h2 style={{ textAlign: "center" }}>You don't have Access for this page</h2>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const showConfig = getUserPermission(state, "WEB_MENU", "SHOW_NOTIFICATIONS_HANDLER_PROPERTIES"),
    editConfig = getUserPermission(state, "ADMN", "ADMN_EDIT_NOTIFICATIONS_HANDLER_PROPERTIES"),
    adminShowConfig = getUserPermission(state, "ADMN", "ADMN_VIEW_NOTIFICATIONS_HANDLER_PROPERTIES");

  return {
    showConfig,
    editConfig,
    adminShowConfig,
  };
};

const mapDispatchToProps = {
  showModal,
  getNotificationProperties,
  updateNotificationProperties,
  testNotificationProperties,
};

export default connect(mapStateToProps, mapDispatchToProps)(NotificationProperties);
